import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

export const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSSOLogin = async () => {
    setLoading(true);
    try {
      // La page navigue vers Keycloak : au succès, ce code ne reprend pas la
      // main. Seul un échec avant la redirection revient ici.
      await login();
    } catch (error) {
      setLoading(false);
      toast.error(error?.message || 'Impossible de joindre Keycloak');
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <Toaster position="top-right" />

      {/* Left Panel - Login */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <Cloud className="w-7 h-7 text-primary-foreground" />
              </div>
              <h1 className="text-3xl font-bold font-[Outfit]">Cost Cloud</h1>
            </div>
            <p className="text-muted-foreground">
              Gérez vos coûts cloud multi-providers en un seul endroit
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Connexion</CardTitle>
              <CardDescription>
                Connectez-vous avec votre compte professionnel ELIKA.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleSSOLogin}
                disabled={loading}
                className="w-full"
                size="lg"
                data-testid="sso-login-btn"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-4">
                Authentification unique ELIKA — Keycloak fédérant Microsoft Entra ID
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Decorative */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{
          // Image servie par l'application : une URL externe serait une
          // dépendance fragile, une requête vers un tiers à chaque connexion,
          // et obligerait à élargir la CSP.
          backgroundImage: 'url(/login-bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-primary/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="text-4xl font-bold font-[Outfit] mb-4">
            Optimisez vos dépenses Cloud
          </h2>
          <p className="text-lg opacity-90 max-w-md">
            Consolidez AWS, Azure, GCP et OVH dans un tableau de bord unique.
            Visualisez, comparez et maîtrisez vos coûts.
          </p>
          <div className="flex gap-4 mt-8">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="w-3 h-3 rounded-full bg-[#FF9900]" />
              <span>AWS</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="w-3 h-3 rounded-full bg-[#007FFF]" />
              <span>Azure</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="w-3 h-3 rounded-full bg-[#4285F4]" />
              <span>GCP</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg">
              <span className="w-3 h-3 rounded-full bg-[#123F6D]" />
              <span>OVH</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
